'use client';

import type { RefObject } from 'react';
import { useCallback } from 'react';
import type { OztMethod } from '../../consts/ozonetel/methods';
import { OZT_METHOD_TYPES } from '../../consts/ozonetel/methods';
import type {
  CallHistory,
  OzonetelInitializePayload,
  OzonetelStartCallPayload,
} from '../../consts/ozonetel/sdk';
import type { OztPhoneNumberPayload, OztSetModePayload } from '../../consts/ozonetel/shared';
import { getOzonetelInitializePayload } from '../../utils/ozonetel/getInitializePayload';
import { getOzonetelSdk } from '../../utils/ozonetel/getSdk';

interface UseOzonetelMethodsParams {
  customerId: string;
  pendingPayloadRef: RefObject<OzonetelInitializePayload | undefined>;
  initializeOzonetel: (payload?: OzonetelInitializePayload) => Promise<void>;
  getInitializedSdk: () => Promise<unknown>;
  handleStartCall: (payload: OzonetelStartCallPayload) => Promise<unknown>;
  handleEndCall: () => Promise<unknown>;
  handleHold: (payload: OztPhoneNumberPayload | undefined) => Promise<unknown>;
  handleUnhold: (payload: OztPhoneNumberPayload | undefined) => Promise<unknown>;
  handleMute: () => Promise<unknown>;
  handleUnmute: () => Promise<unknown>;
  handleMuteAgent: () => void;
  handleUnmuteAgent: () => void;
  handleSetMode: (payload: OztSetModePayload) => Promise<unknown>;
  handleLogout: () => Promise<unknown>;
  handleReleaseAgent: () => Promise<unknown>;
  handleGetCallRecording: () => Promise<CallHistory[]>;
}

export function useOzonetelMethods({
  customerId,
  pendingPayloadRef,
  initializeOzonetel,
  getInitializedSdk,
  handleStartCall,
  handleEndCall,
  handleHold,
  handleUnhold,
  handleMute,
  handleUnmute,
  handleMuteAgent,
  handleUnmuteAgent,
  handleSetMode,
  handleLogout,
  handleReleaseAgent,
  handleGetCallRecording,
}: UseOzonetelMethodsParams) {
  const handleOztMethod = useCallback(
    async (event: OztMethod) => {
      switch (event.eventType) {
        case OZT_METHOD_TYPES.initialize: {
          const initializePayload = getOzonetelInitializePayload({
            customerId,
            payload: event.eventPayload,
          });
          if (!initializePayload) {
            console.error('OZT initialize method is missing agentid, phoneNumber, or pin');
            return;
          }
          pendingPayloadRef.current = initializePayload;
          await initializeOzonetel(initializePayload);
          return;
        }
        case OZT_METHOD_TYPES.startCall:
          await handleStartCall(event.eventPayload);
          return;
        case OZT_METHOD_TYPES.endCall:
          await handleEndCall();
          return;
        case OZT_METHOD_TYPES.hold:
          await handleHold(event.eventPayload);
          return;
        case OZT_METHOD_TYPES.unhold:
          await handleUnhold(event.eventPayload);
          return;
        case OZT_METHOD_TYPES.mute:
          await handleMute();
          return;
        case OZT_METHOD_TYPES.unmute:
          await handleUnmute();
          return;
        case OZT_METHOD_TYPES.muteAgent:
          handleMuteAgent();
          return;
        case OZT_METHOD_TYPES.unmuteAgent:
          handleUnmuteAgent();
          return;
        case OZT_METHOD_TYPES.setMode:
          await handleSetMode(event.eventPayload);
          return;
        case OZT_METHOD_TYPES.logout:
          await handleLogout();
          return;
        case OZT_METHOD_TYPES.getCallData:
          await getInitializedSdk();
          getOzonetelSdk()?.getCallData?.();
          return;
        case OZT_METHOD_TYPES.releaseAgent:
          await handleReleaseAgent();
          return;
        case OZT_METHOD_TYPES.getCallRecording:
          await handleGetCallRecording();
          return;
      }
    },
    [
      customerId,
      getInitializedSdk,
      handleEndCall,
      handleHold,
      handleLogout,
      handleMute,
      handleMuteAgent,
      handleSetMode,
      handleStartCall,
      handleUnhold,
      handleUnmute,
      handleUnmuteAgent,
      handleReleaseAgent,
      handleGetCallRecording,
      initializeOzonetel,
      pendingPayloadRef,
    ],
  );

  return { handleOztMethod } as const;
}
